import { useQuery } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import { useState } from 'react';
import { allConfigs } from 'src/api/configurations';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import type { ActionItem } from 'src/constants';
import { DOWNLOAD_MARKERS, MENU_GROUPS } from 'src/constants';
import { cleanPayload } from 'src/helpers/common';

const useStyles = makeStyles(() => ({
  actionsButtonContainer: {
    cursor: 'pointer',
  },
  actionsButton: {
    minWidth: 0,
  },
}));

const DOWNLOAD = 'Download';
const EDIT = 'Edit';
const DELETE = 'Delete';

interface MarkerItem {
  id: string;
  markIn?: string;
  markOut?: string;
  title?: string;
  description?: string;
}

interface ConfigNode {
  optionKey: string;
  optionValue: string;
  [key: string]: unknown;
}

interface MarkersActionsButtonProps {
  handleEdit: (item: MarkerItem) => void;
  handleDelete: (item: MarkerItem) => void;
  handleDownload: (item: MarkerItem) => void;
  markerItem: MarkerItem;
  isAwsMedia: boolean;
}

const MarkersActionsButton: React.FC<MarkersActionsButtonProps> = ({
  handleEdit,
  handleDelete,
  handleDownload,
  markerItem,
  isAwsMedia,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const { data: downloadMarkers } = useQuery(allConfigs, {
    variables: {
      optionKey: DOWNLOAD_MARKERS,
    },
    fetchPolicy: 'cache-and-network',
  });

  const downloadMarkersCleaned = cleanPayload(downloadMarkers) as ConfigNode[];

  const buildActions = (): ActionItem[] => {
    const editAction: ActionItem = { label: EDIT, icon: <EditNoteIcon />, group: MENU_GROUPS.MANAGE };
    const downloadAction: ActionItem = {
      label: DOWNLOAD,
      icon: <DownloadIcon />,
      disabled: !markerItem.markOut,
      group: MENU_GROUPS.EXPORT,
    };
    const deleteAction: ActionItem = { label: DELETE, icon: <DeleteIcon />, group: MENU_GROUPS.DANGER };

    if (isAwsMedia || downloadMarkersCleaned[0]?.optionValue === '0') {
      return [editAction, deleteAction];
    }
    return [editAction, downloadAction, deleteAction];
  };

  const actions = buildActions();

  const handleToggle = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAction = (action: ActionItem): void => {
    if (action.label === DOWNLOAD) {
      handleDownload(markerItem);
    } else if (action.label === DELETE) {
      handleDelete(markerItem);
    } else if (action.label === EDIT) {
      handleEdit(markerItem);
    }
    setAnchorEl(null);
  };

  return (
    <div className={classes.actionsButtonContainer}>
      <IconButton onClick={handleToggle} className={classes.actionsButton} size="large">
        <MoreVert />
      </IconButton>
      <StyledContextMenu
        actions={actions}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        onAction={handleAction}
        placement="left-start"
        testIdPrefix="cyp-marker-action"
      />
    </div>
  );
};

export default MarkersActionsButton;
